import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { cn } from "~/common/shadcn-utils"
import XIcon from "~/images/icons/x.svg?react"
import { CandidateTestUnlockRequestStateEnum } from "~/__generated__/graphql"
import {
  CandidateTestsFilterDropdown,
  FilterOption,
} from "./CandidateTestsFilterDropdown"
import { Search } from "lucide-react"
import { useDebouncedCallback } from "use-debounce"

export interface UnlockRequestFilter {
  unlockRequestStates: CandidateTestUnlockRequestStateEnum[] | null
  query: string | null
}

export const CandidateTestUnlockRequestsTableTopFilter = ({
  filter,
  onFilterChange,
  searchParamsPrefix = "requests_",
}: {
  filter: UnlockRequestFilter
  onFilterChange: (filter: UnlockRequestFilter) => void
  searchParamsPrefix?: string
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState(filter.query || "")

  const handleFilterChange = (newFilter: UnlockRequestFilter) => {
    searchParams.delete(`${searchParamsPrefix}page`)
    setSearchParams(searchParams)
    onFilterChange(newFilter)
  }

  const debouncedOnQueryChanged = useDebouncedCallback((value: string) => {
    handleFilterChange({ ...filter, query: value || null })
  }, 300)

  const clearFilters = () => {
    handleFilterChange({
      ...filter,
      unlockRequestStates: [],
      query: null,
    })
  }

  const stateFilterOptionMap: Record<
    CandidateTestUnlockRequestStateEnum,
    string
  > = {
    [CandidateTestUnlockRequestStateEnum.Pending]: "Pending",
    [CandidateTestUnlockRequestStateEnum.Approved]: "Approved",
    [CandidateTestUnlockRequestStateEnum.Denied]: "Denied",
  }

  const stateFilterOptions = Object.entries(stateFilterOptionMap).map(
    ([key, value]) => ({
      id: key as CandidateTestUnlockRequestStateEnum,
      name: value,
    })
  )

  const onStateFilterChanged = (selectedOptions: FilterOption[]) => {
    const newStates = selectedOptions.map(
      (option) => option.id as CandidateTestUnlockRequestStateEnum
    )
    handleFilterChange({ ...filter, unlockRequestStates: newStates })
  }

  const selectedStateFilterOptions =
    filter.unlockRequestStates && filter.unlockRequestStates.length > 0
      ? filter.unlockRequestStates.map(
          (id) => ({ id: id, name: id }) as FilterOption
        )
      : []

  return (
    <div className="flex flex-row items-center justify-between">
      <div className="flex items-center pr-6">
        <label className="block relative">
          <Search
            color="#999999"
            size={16}
            className="absolute top-[10px] left-[10px]"
          />
          <input
            className={cn(
              "border-1 border-gray-E3E2E0 rounded w-[250px] pl-10"
            )}
            placeholder="Search by name or email"
            value={query || ""}
            autoFocus
            onChange={(e) => {
              const value = e.target.value
              setQuery(value)
              debouncedOnQueryChanged(value)
            }}
          />
        </label>
      </div>
      <div className="flex flex-row items-center gap-1">
        {filter.unlockRequestStates &&
          filter.unlockRequestStates.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="flex flex-row gap-1 items-center text-[#F14C76] p-4 text-sm"
            >
              <XIcon className="h-4 w-4" />
              <span>Clear Filters</span>
            </button>
          )}

        <CandidateTestsFilterDropdown
          filterOptions={stateFilterOptions}
          selectedFilterOptions={selectedStateFilterOptions}
          onFilterChange={onStateFilterChanged}
          loading={false}
          buttonLabel="Request Status"
          popoverHeader="Filter by request status"
          negateFilterLabel="All statuses"
          allowMultiple={true}
        />
      </div>
    </div>
  )
}
